/**
 * Locale Service - Manages internationalization (i18n) for the application
 *
 * Responsibilities:
 * - Loads locale files based on customer's defaultLanguage
 * - Provides translation function with ICU MessageFormat support
 * - Updates document language attributes (lang, dir) for accessibility
 * - Caches locale data in module scope for fast access
 *
 * Usage:
 * ```javascript
 * import { getLocaleService } from './service-loader';
 *
 * const locale = await getLocaleService();
 * await locale.initialize();
 *
 * // Simple translation
 * const saveLabel = locale.t('save'); // "Save"
 *
 * // With ICU MessageFormat variables
 * const itemCount = locale.t('item_count', { count: 5 }); // "5 items"
 * ```
 */

import { loadLocale } from '../locales/index';
import { IntlMessageFormat } from 'intl-messageformat';

/**
 * Tracks whether SYSTEM_ADMIN-only phrases have been merged into the
 * current locale. Module-scoped so repeated calls from different admin
 * surfaces on the same page don't re-fetch the chunk.
 */
let _systemAdminPhrasesLoadedFor = null; // language code or null

/**
 * Current locale file (module-scoped for fast access)
 * @type {import('../locales/types').LocaleFile | null}
 */
let _currentLocale = null;

/**
 * LocalStorage key for cached language metadata (customer default)
 */
const LOCALE_STORAGE_KEY = 'metalark_locale_metadata';

/**
 * LocalStorage key for user's explicit language preference (login page selection)
 */
const USER_LANGUAGE_STORAGE_KEY = 'metalark_locale_login_language';

export const localeService = {
    /**
     * Initialize the locale service by reading user's language preference from localStorage,
     * falling back to customer's defaultLanguage if no preference is set.
     *
     * Language preference priority:
     * 1. User's explicit choice (metalark_locale_login_language) - highest priority
     * 2. Customer's defaultLanguage (metalark_locale_metadata)
     * 3. English fallback
     *
     * This ensures user's language selection persists across login/logout and is not
     * overwritten by the customer default.
     *
     * @returns {Promise<void>}
     */
    async initialize() {
        console.log('[LocaleService] Initializing locale service');

        try {
            const { store } = await import('./store');
            const state = store.getState();

            // Check if user is authenticated (has customer data)
            const isAuthenticated = Boolean(state.customer?.customerId);

            // Priority 1: Check for user's explicit language preference
            const userPreference = this._getUserLanguagePreference();
            if (userPreference?.languageCode) {
                console.log(`[LocaleService] Loading user's explicit language preference: ${userPreference.languageCode}`);
                await this.setLocale(userPreference.languageCode, false);
                return;
            }

            // Priority 2 & 3: Fall back to customer default or English
            if (isAuthenticated) {
                // Authenticated: Use customer's defaultLanguage
                const code = state.customer?.defaultLanguage || 'en';
                console.log(`[LocaleService] Authenticated - Loading locale from customer: ${code}`);
                await this.setLocale(code, true); // true = save to localStorage
            } else {
                // Not authenticated: Try cached customer default, fallback to English
                const cachedLocale = this._getLocaleFromStorage();
                const code = cachedLocale?.languageCode || 'en';
                console.log(`[LocaleService] Not authenticated - Loading locale from ${cachedLocale ? 'cache' : 'fallback'}: ${code}`);
                await this.setLocale(code, false);
            }
        } catch (error) {
            console.error('[LocaleService] Failed to initialize, falling back to English:', error);
            await this.setLocale('en', false);
        }
    },

    /**
     * Load and set a new locale by language code.
     *
     * Updates:
     * - Internal current locale cache
     * - document.documentElement.dir (ltr/rtl)
     * - document.documentElement.lang (for screen readers)
     * - localStorage (if saveToStorage is true)
     *
     * Falls back to English if the requested locale cannot be loaded.
     *
     * @param {string} languageCode - ISO 639-1 language code (e.g., "en", "es", "fr")
     * @param {boolean} [saveToStorage=false] - Whether to save locale metadata to localStorage
     * @returns {Promise<void>}
     */
    async setLocale(languageCode, saveToStorage = false) {
        console.log(`[LocaleService] Setting locale to: ${languageCode} (save to storage: ${saveToStorage})`);

        try {
            // Load the requested locale, or fall back to English
            const locale = (await loadLocale(languageCode)) || (await loadLocale('en'));

            if (!locale) {
                console.error('[LocaleService] CRITICAL: Failed to load English fallback locale');
                return;
            }

            _currentLocale = locale;

            // Locale changed — any previously-merged admin phrases are now
            // for a stale language. Reset the tracker so the next call to
            // loadSystemAdminMessages() re-fetches the right language.
            _systemAdminPhrasesLoadedFor = null;

            // Update document attributes for accessibility and CSS
            document.documentElement.dir = locale.direction;
            document.documentElement.lang = locale.languageCode;

            // Save to localStorage if requested (after successful authentication)
            if (saveToStorage) {
                this._saveLocaleToStorage(locale);
            }

            console.log(`[LocaleService] Locale set successfully:`, {
                code: locale.languageCode,
                name: locale.languageName,
                direction: locale.direction,
                phraseCount: Object.keys(locale.phrases).length,
                cached: saveToStorage
            });
        } catch (error) {
            console.error('[LocaleService] Failed to set locale:', error);
            // Don't throw - app should continue with fallback locale
        }
    },

    /**
     * Merge SYSTEM_ADMIN-only phrases into the current locale.
     *
     * Call this from admin-only components' onMounted() BEFORE the first
     * `locale.t('admin_...')` evaluates. The file lives in its own chunk at
     * /assets/locales-system-admin/ which Lambda@Edge gates to SYSTEM_ADMIN
     * requests only (see constructs/GatewayConstruct/lambdas/jwt-validator.ts);
     * dev/mock has no edge gate, so the physical file separation is also our
     * only in-dev control.
     *
     * Idempotent — repeated calls for the same locale are no-ops.
     *
     * @returns {Promise<boolean>} true if admin phrases are now merged, false
     *   if the current locale has no admin file (caller's `locale.t(key)`
     *   will fall back to returning the key string itself, same as any other
     *   missing translation).
     */
    async loadSystemAdminMessages() {
        const code = this.getLanguageCode();
        if (_systemAdminPhrasesLoadedFor === code) return true;

        try {
            const { loadSystemAdminLocale } = await import('../locales/system-admin/index');
            const adminLocale = (await loadSystemAdminLocale(code))
                // Admin file for the active language may not exist yet. Fall
                // back to English rather than skipping entirely so the admin
                // UI still renders (admin staff read English anyway).
                || (code !== 'en' ? await loadSystemAdminLocale('en') : null);

            if (!adminLocale || !_currentLocale) {
                console.warn(`[LocaleService] No system-admin phrases available for ${code}`);
                return false;
            }

            _currentLocale.phrases = { ..._currentLocale.phrases, ...adminLocale.phrases };
            _systemAdminPhrasesLoadedFor = code;
            console.log(`[LocaleService] Merged system-admin phrases for ${code}:`, {
                added: Object.keys(adminLocale.phrases).length,
            });
            return true;
        } catch (error) {
            console.error('[LocaleService] Failed to load system-admin phrases:', error);
            return false;
        }
    },

    /**
     * Translate a phrase key with optional variables for ICU MessageFormat.
     *
     * Returns the key itself if the phrase is not found (no error thrown).
     * Logs a warning to help identify missing translations during development.
     *
     * @param {string} key - Translation key (e.g., "save", "item_count")
     * @param {Record<string, any>} [values] - Optional variables for ICU MessageFormat (e.g., { count: 5 })
     * @returns {string} Translated phrase, or the key itself if not found
     *
     * @example
     * ```javascript
     * locale.t('save') // "Save"
     * locale.t('item_count', { count: 0 }) // "No items"
     * locale.t('item_count', { count: 1 }) // "1 item"
     * locale.t('item_count', { count: 5 }) // "5 items"
     * locale.t('missing_key') // "missing_key" (with console warning)
     * ```
     */
    t(key, values) {
        const phrase = _currentLocale?.phrases?.[key];

        if (!phrase) {
            console.warn(`[LocaleService] Missing translation key: ${key}`);
            return key;
        }

        // If no variables provided, return phrase as-is (most common case)
        if (!values) {
            return phrase;
        }

        // Use ICU MessageFormat for pluralization, number formatting, etc.
        // ignoreTag: true keeps `<strong>`, `<br>`, etc. as literal text rather
        // than parsing them as rich-text tags. Our phrases embed real HTML that
        // is rendered via the <raw> component; without this flag any phrase that
        // mixes a tag with a placeholder (e.g. `<strong>{email}</strong>`) throws
        // "context variable not provided", falls through to the catch below, and
        // returns the phrase with its {placeholders} unsubstituted.
        try {
            const formatter = new IntlMessageFormat(phrase, _currentLocale.languageCode, undefined, { ignoreTag: true });
            return formatter.format(values);
        } catch (error) {
            console.error(`[LocaleService] Failed to format phrase "${key}":`, error);
            return phrase; // Return unformatted phrase as fallback
        }
    },

    /**
     * Get the current locale's text direction.
     *
     * @returns {'ltr' | 'rtl'} Text direction (left-to-right or right-to-left)
     */
    getDirection() {
        return _currentLocale?.direction || 'ltr';
    },

    /**
     * Get the current locale's language code.
     *
     * @returns {string} ISO 639-1 language code (e.g., "en", "es")
     */
    getLanguageCode() {
        return _currentLocale?.languageCode || 'en';
    },

    /**
     * Get the current locale's language name.
     *
     * @returns {string} Human-readable language name (e.g., "English", "Español")
     */
    getLanguageName() {
        return _currentLocale?.languageName || 'English';
    },

    /**
     * Check if a translation key exists in the current locale.
     *
     * @param {string} key - Translation key to check
     * @returns {boolean} True if the key exists
     */
    hasKey(key) {
        return Boolean(_currentLocale?.phrases?.[key]);
    },

    /**
     * Toggle pseudo-locale for i18n QA testing.
     * Switches to the pseudo-locale (accented + padded English) to reveal
     * missed string extractions, layout overflow, and concatenation issues.
     * Call again to switch back to English. Not persisted to localStorage.
     *
     * Usage from browser console: window.locale.togglePseudo()
     */
    async togglePseudo() {
        const isPseudo = _currentLocale?.languageCode === 'pseudo';
        await this.setLocale(isPseudo ? 'en' : 'pseudo', false);
        console.log(`[LocaleService] Pseudo-locale ${isPseudo ? 'OFF' : 'ON'}`);
    },

    /**
     * Get list of all available languages.
     *
     * Returns metadata for all available locale files. As new locale files are added,
     * they should be added to this list.
     *
     * @returns {Array<{languageCode: string, languageName: string, direction: 'ltr'|'rtl'}>}
     */
    getAvailableLanguages() {
        return [
            { languageCode: 'en', languageName: 'English', direction: 'ltr' },
            { languageCode: 'es', languageName: 'Español', direction: 'ltr' },
            { languageCode: 'pt', languageName: 'Português', direction: 'ltr' },
            { languageCode: 'fr', languageName: 'Français', direction: 'ltr' },
            { languageCode: 'de', languageName: 'Deutsch', direction: 'ltr' },
            { languageCode: 'ar', languageName: 'العربية', direction: 'rtl' },
            { languageCode: 'he', languageName: 'עברית', direction: 'rtl' },
            { languageCode: 'zh', languageName: '中文', direction: 'ltr' },
            { languageCode: 'ja', languageName: '日本語', direction: 'ltr' },
            { languageCode: 'ko', languageName: '한국어', direction: 'ltr' },
            { languageCode: 'hi', languageName: 'हिन्दी', direction: 'ltr' },
        ];
    },

    /**
     * Save locale metadata to localStorage for use before authentication.
     * Stores all locale properties EXCEPT phrases (which would be too large).
     * This future-proofs the code - any new metadata fields added to LocaleFile
     * will automatically be cached without code changes.
     *
     * @private
     * @param {import('../locales/types').LocaleFile} locale - Full locale object to cache
     */
    _saveLocaleToStorage(locale) {
        try {
            // Create a shallow copy and delete the phrases property
            const metadata = { ...locale };
            delete metadata.phrases;

            localStorage.setItem(LOCALE_STORAGE_KEY, JSON.stringify(metadata));
            console.log('[LocaleService] Saved locale metadata to localStorage:', metadata.languageCode);
        } catch (error) {
            console.warn('[LocaleService] Failed to save locale to localStorage:', error);
            // Non-critical - continue without caching
        }
    },

    /**
     * Retrieve cached locale metadata from localStorage.
     * Used for public/login pages before user is authenticated.
     *
     * @private
     * @returns {import('../locales/types').LocaleMetadata | null} Cached metadata or null if not found
     */
    _getLocaleFromStorage() {
        try {
            const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
            if (stored) {
                const metadata = JSON.parse(stored);
                console.log('[LocaleService] Retrieved locale metadata from localStorage:', metadata.languageCode);
                return metadata;
            }
        } catch (error) {
            console.warn('[LocaleService] Failed to read locale from localStorage:', error);
        }
        return null;
    },

    /**
     * Set user's explicit language preference.
     * This updates localStorage and applies the new locale immediately.
     * Use this when user explicitly selects a language (login page, navbar selector).
     *
     * After calling this, the page should be reloaded to re-initialize all components.
     *
     * @param {string} languageCode - ISO 639-1 language code (e.g., "en", "es", "fr")
     * @returns {Promise<void>}
     */
    async setUserLanguagePreference(languageCode) {
        console.log(`[LocaleService] Setting user language preference to: ${languageCode}`);

        // Load the locale to get full metadata
        const locale = await loadLocale(languageCode);
        if (!locale) {
            console.error(`[LocaleService] Failed to load locale: ${languageCode}`);
            return;
        }

        // Save to user preference storage. Intentionally do NOT call
        // setLocale here — the only caller (language-selector) follows
        // this with window.location.reload(), and applying the new locale
        // beforehand would flip document.documentElement.dir while the
        // page still shows the old-language text, producing a visible
        // RTL/LTR flash. Page-init reads this preference and applies the
        // locale once on the fresh load.
        this._saveUserLanguagePreference(locale);
    },

    /**
     * Check if user's language preference differs from customer's default language.
     * Used to determine whether to show reconciliation modal or navbar language selector.
     *
     * @returns {{hasMismatch: boolean, userLanguage: string, customerLanguage: string, userLanguageName: string, customerLanguageName: string}}
     */
    hasLanguageMismatch() {
        const userPreference = this._getUserLanguagePreference();
        const userLanguage = userPreference?.languageCode;

        // If no user preference set, there's no mismatch
        if (!userLanguage) {
            return {
                hasMismatch: false,
                userLanguage: null,
                customerLanguage: null,
                userLanguageName: null,
                customerLanguageName: null
            };
        }

        // Get customer default from storage (set during authentication)
        const customerDefault = this._getLocaleFromStorage();
        const customerLanguage = customerDefault?.languageCode || 'en';

        const hasMismatch = userLanguage !== customerLanguage;

        return {
            hasMismatch,
            userLanguage,
            customerLanguage,
            userLanguageName: userPreference?.languageName || userLanguage,
            customerLanguageName: customerDefault?.languageName || customerLanguage
        };
    },

    /**
     * Retrieve user's explicit language preference from localStorage.
     * This takes precedence over customer default language.
     *
     * @private
     * @returns {import('../locales/types').LocaleMetadata | null} User's language preference or null
     */
    _getUserLanguagePreference() {
        try {
            const stored = localStorage.getItem(USER_LANGUAGE_STORAGE_KEY);
            if (stored) {
                const metadata = JSON.parse(stored);
                console.log('[LocaleService] Retrieved user language preference from localStorage:', metadata.languageCode);
                return metadata;
            }
        } catch (error) {
            console.warn('[LocaleService] Failed to read user language preference from localStorage:', error);
        }
        return null;
    },

    /**
     * Save user's explicit language preference to localStorage.
     * Stores all locale properties EXCEPT phrases.
     *
     * @private
     * @param {import('../locales/types').LocaleFile} locale - Full locale object to cache
     */
    _saveUserLanguagePreference(locale) {
        try {
            // Create a shallow copy and delete the phrases property
            const metadata = { ...locale };
            delete metadata.phrases;

            localStorage.setItem(USER_LANGUAGE_STORAGE_KEY, JSON.stringify(metadata));
            console.log('[LocaleService] Saved user language preference to localStorage:', metadata.languageCode);
        } catch (error) {
            console.warn('[LocaleService] Failed to save user language preference to localStorage:', error);
            // Non-critical - continue without caching
        }
    },

    /**
     * Clear user's explicit language preference from localStorage.
     * Call this when user's preference matches the customer default (no override needed).
     */
    clearUserLanguagePreference() {
        try {
            localStorage.removeItem(USER_LANGUAGE_STORAGE_KEY);
            console.log('[LocaleService] Cleared user language preference from localStorage');
        } catch (error) {
            console.warn('[LocaleService] Failed to clear user language preference from localStorage:', error);
        }
    }
};

// Expose globally for console debugging
// Usage in browser console:
//   locale.t('save')                          // "Save"
//   locale.t('item_count', { count: 5 })      // "5 items"
//   locale.getLanguageCode()                  // "en"
//   await locale.setLocale('es')              // Switch to Spanish
window.locale = localeService;

// Export as 'locale' for convenience in imports
export const locale = localeService;
